import { type PoExport, poGetExportDownload } from '@repo/sdk/gen'
import { zPoListExportsData } from '@repo/sdk/zod'
import { Badge } from '@repo/ui/badge'
import { Button } from '@repo/ui/button'
import {
	DataTable,
	useDataTableColumnState
} from '@repo/ui/commons/data-table/data-table'
import type {
	DataTableColumn,
	DataTableSort
} from '@repo/ui/commons/data-table/types'
import { IdCell } from '@repo/ui/commons/id-cell'
import { formatLocaleDate } from '@repo/ui/dates'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '@repo/ui/dropdown-menu'
import { downloadFile, formatError } from '@repo/ui/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
	FileDown,
	FileSpreadsheet,
	FileText,
	MoreHorizontal
} from 'lucide-react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { useTranslation } from '@/lib/i18n'
import { poExportsListQueryOptions } from '@/routes/(private)/documents/-lib/query-options'
import { usePageSize } from '@/stores/use-page-size'

const EMPTY_EXPORTS: PoExport[] = []
// The fields the export history can sort on, as the backend declares them.
const EXPORT_SORT_FIELDS = zPoListExportsData.shape.body.shape.sort
	.unwrap()
	.shape.by.unwrap().enum

// The export history, identical wherever it is shown: the whole archive on its
// own page, or one document's exports inside that document. The caller owns
// the URL state, this owns the table and the downloads.
function ExportsTable(props: {
	runId?: string
	status?: 'pending' | 'completed' | 'failed'
	page: number
	sort: DataTableSort<keyof typeof EXPORT_SORT_FIELDS>
	onPageChange: (page: number) => void
	onSortChange: (sort: DataTableSort<keyof typeof EXPORT_SORT_FIELDS>) => void
}) {
	const { t } = useTranslation()
	const pageSize = usePageSize((s) => s.pageSize)
	const setPageSize = usePageSize((s) => s.setPageSize)

	const query = useQuery(
		poExportsListQueryOptions({
			page: props.page,
			limit: pageSize,
			filters: {
				status: props.status ? [props.status] : undefined,
				runId: props.runId
			},
			sort: props.sort.by
				? {
						by: props.sort.by,
						direction: props.sort.direction ?? 'asc'
					}
				: undefined
		})
	)

	const download = useMutation({
		mutationFn: async (body: { id: string; file: 'xlsx' | 'csv' }) => {
			const response = await poGetExportDownload({
				body: { filters: { id: body.id } }
			})
			return body.file === 'xlsx'
				? response.data.xlsxUrl
				: response.data.csvUrl
		},
		onSuccess: (url) => {
			if (url) {
				downloadFile(url)
			}
		},
		onError: (error) => toast.error(formatError(error))
	})

	const statusBadges: Record<PoExport['status'], ReactNode> = {
		pending: <Badge variant='outline'>{t('po:exports.pending')}</Badge>,
		completed: (
			<Badge variant='secondary'>{t('po:exports.completed')}</Badge>
		),
		failed: <Badge variant='destructive'>{t('po:exports.failed')}</Badge>
	}

	const columns: DataTableColumn<
		PoExport,
		keyof typeof EXPORT_SORT_FIELDS
	>[] = [
		{
			id: 'id',
			header: t('po:exports.columns.id'),
			pinned: 'left',
			cell: (dataItem) => <IdCell id={dataItem.id} />,
			minWidth: 240,
			flex: 1
		},
		// Inside a document every row would repeat its name.
		...(props.runId
			? []
			: [
					{
						id: 'runName',
						header: t('po:exports.columns.document'),
						pinned: 'left' as const,
						cell: (dataItem: PoExport) => (
							<Link
								className='truncate font-medium hover:underline'
								params={{ runId: dataItem.runId }}
								to='/documents/$runId'
							>
								{dataItem.runName}
							</Link>
						),
						minWidth: 220,
						flex: 2
					}
				]),
		{
			id: 'generatedAt',
			header: t('po:exports.columns.generated_at'),
			sortKey: EXPORT_SORT_FIELDS.generatedAt,
			cell: (dataItem) =>
				formatLocaleDate(
					dataItem.generatedAt ?? dataItem.createdAt,
					'P HH:mm'
				),
			minWidth: 150,
			flex: 1
		},
		{
			id: 'status',
			header: t('po:exports.columns.status'),
			cell: (dataItem) => statusBadges[dataItem.status],
			minWidth: 110,
			flex: 1
		},
		{
			id: 'summary',
			header: t('po:exports.columns.summary'),
			cell: (dataItem) => {
				const summary = dataItem.summary as {
					titles?: number
					copies?: number
					publishers?: number
				} | null
				return summary?.titles !== undefined
					? t('po:exports.summary_line', {
							titles: summary.titles,
							copies: summary.copies ?? 0,
							publishers: summary.publishers ?? 0
						})
					: '-'
			},
			minWidth: 260,
			flex: 2
		},
		{
			id: 'actions',
			header: t('po:exports.columns.actions'),
			pinned: 'right',
			hideable: false,
			manageable: false,
			pinnable: false,
			enableResizing: false,
			cell: (dataItem) =>
				dataItem.status === 'completed' ? (
					<div className='flex w-full justify-end'>
						<DropdownMenu>
							<DropdownMenuTrigger
								render={
									<Button
										aria-label={t(
											'po:exports.columns.actions'
										)}
										disabled={download.isPending}
										size='icon-sm'
										variant='ghost'
									/>
								}
							>
								<MoreHorizontal />
							</DropdownMenuTrigger>
							<DropdownMenuContent align='end'>
								<DropdownMenuItem
									onClick={() =>
										download.mutate({
											id: dataItem.id,
											file: 'xlsx'
										})
									}
								>
									<FileSpreadsheet />
									{t('po:exports.download_xlsx')}
								</DropdownMenuItem>
								<DropdownMenuItem
									onClick={() =>
										download.mutate({
											id: dataItem.id,
											file: 'csv'
										})
									}
								>
									<FileText />
									{t('po:exports.download_csv')}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				) : null,
			width: 88,
			minWidth: 88,
			maxWidth: 88
		}
	]

	const columnsState = useDataTableColumnState(columns)

	return (
		<DataTable
			columns={columns}
			columnsState={columnsState}
			data={query.data?.data ?? EMPTY_EXPORTS}
			pagination={{
				pageIndex: props.page - 1,
				pageCount: query.data?.meta.totalPages ?? 1,
				onPageChange: (next) => props.onPageChange(next + 1),
				pageSize,
				onPageSizeChange: (next) => {
					setPageSize(next)
					props.onPageChange(1)
				}
			}}
			sort={{
				state: { by: props.sort.by, direction: props.sort.direction },
				onSortChange: props.onSortChange
			}}
			state={{
				loading: query.isPending,
				error: query.isError,
				empty: {
					icon: <FileDown />,
					title: t('po:exports.empty_title'),
					description: t(
						props.runId
							? 'po:exports.empty_document_description'
							: 'po:exports.empty_description'
					)
				}
			}}
		/>
	)
}

export { EXPORT_SORT_FIELDS, ExportsTable }
